import React, { useState, useContext } from 'react';
import {
  Box,
  VStack,
  FormControl,
  FormLabel,
  Input,
  Button,
  Heading,
  Text,
  useToast,
  FormErrorMessage,
  Textarea,
  Select,
  HStack,
  Icon,
  Progress,
  Flex,
  Container,
  Divider,
  Stack,
  Badge,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Stepper,
  Step,
  StepIndicator,
  StepStatus,
  StepIcon,
  StepNumber,
  StepTitle,
  StepDescription,
  StepSeparator,
  useSteps
} from '@chakra-ui/react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { FiBriefcase, FiMapPin, FiPhone, FiGlobe, FiFileText, FiCheckCircle, FiX } from 'react-icons/fi';
import axiosInstance from '../../../services/axios';
import { useAuth } from '../../../hooks/useAuth';
import { AuthContext } from '../../../context/JWTAuthContext';
import DocumentUploadCard from '../../Common/DocumentUploadCard';

const CompanyInfoStep = ({ onComplete, onBack }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [documents, setDocuments] = useState([]);
  const [uploadProgress, setUploadProgress] = useState({});
  const [uploadErrors, setUploadErrors] = useState({});
  const [currentStep, setCurrentStep] = useState(0);
  const toast = useToast();
  const navigate = useNavigate();
  const { fetchUser } = useAuth();
  const { authenticateWithCookies, updateUserData } = useContext(AuthContext);

  const steps = [
    { title: 'Company Info', description: 'Basic business details' },
    { title: 'Documents', description: 'Upload verification documents' },
    { title: 'Review', description: 'Confirm and submit' }
  ];

  const { activeStep } = useSteps({
    index: currentStep,
    count: steps.length,
  });

  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
    watch
  } = useForm({
    mode: 'onChange'
  });

  const watchedFields = watch();
  const completionPercentage = Math.round(
    (Object.values(watchedFields).filter(value => value && value.trim() !== '').length / 3) * 100
  );

  const documentTypes = [
    {
      key: 'business_license',
      label: 'Business License',
      required: true,
      description: 'Valid business license or registration certificate'
    },
    {
      key: 'tax_certificate',
      label: 'Tax Certificate',
      required: true,
      description: 'Tax registration certificate or EIN documentation'
    },
    {
      key: 'identity_proof',
      label: 'Identity Proof',
      required: true,
      description: 'Government-issued ID (passport, driver\'s license, etc.)'
    },
    {
      key: 'bank_statement',
      label: 'Bank Statement',
      required: false,
      description: 'Recent bank statement showing business account'
    },
    {
      key: 'incorporation_certificate',
      label: 'Incorporation Certificate',
      required: false,
      description: 'Certificate of incorporation or business formation'
    }
  ];

  const handleDocumentUpload = async (documentType, files) => {
    const formData = new FormData();
    formData.append('document_type', documentType);

    Array.from(files).forEach(file => {
      formData.append('files', file);
    });

    try {
      setUploadProgress(prev => ({ ...prev, [documentType]: 0 }));
      setUploadErrors(prev => ({ ...prev, [documentType]: null }));

      const response = await axiosInstance.post('/merchants/documents/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (progressEvent) => {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(prev => ({ ...prev, [documentType]: progress }));
        }
      });

      // Update documents state
      setDocuments(prev => {
        const filtered = prev.filter(doc => doc.document_type !== documentType);
        return [...filtered, ...response.data.uploaded_documents];
      });

      setUploadProgress(prev => ({ ...prev, [documentType]: 100 }));

      toast({
        title: 'Documents Uploaded',
        description: `${files.length} document(s) uploaded successfully`,
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

      // Clear progress after delay
      setTimeout(() => {
        setUploadProgress(prev => ({ ...prev, [documentType]: undefined }));
      }, 2000);

    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Failed to upload documents';
      setUploadErrors(prev => ({ ...prev, [documentType]: errorMessage }));
      setUploadProgress(prev => ({ ...prev, [documentType]: undefined }));

      toast({
        title: 'Upload Failed',
        description: errorMessage,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const getUploadedDocuments = (documentType) => {
    return documents.filter(doc => doc.document_type === documentType);
  };

  const getRequiredDocumentsUploaded = () => {
    const requiredTypes = documentTypes.filter(doc => doc.required).map(doc => doc.key);
    return requiredTypes.every(type => getUploadedDocuments(type).length > 0);
  };

  const onSubmit = async (data) => {
    setIsLoading(true);
    try {
      const response = await axiosInstance.post('/merchants/complete-profile', {
        company_name: data.companyName,
        business_address: data.businessAddress,
        company_registration_number: data.registrationNumber || null,
        industry: data.industry || null,
        tax_id: data.taxId || null,
        website: data.website || null,
        phone: data.phone || null,
      });

      // Update user data directly from response to avoid race condition
      if (response.data.updated_user) {
        updateUserData(response.data.updated_user);
      } else {
        // Fallback to refreshing user data if updated_user not provided
        await authenticateWithCookies();
      }

      toast({
        title: 'Company Profile Completed!',
        description: 'Your merchant account is now set up and ready to use.',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

      // Navigate directly now that backend properly sets profile completion status
      if (onComplete) {
        onComplete(response.data);
      } else {
        navigate('/merchant/dashboard', { replace: true });
      }

    } catch (error) {
      console.error('Company info submission error:', error);

      const errorMessage = error.response?.data?.detail ||
                          'Failed to complete company profile. Please try again.';

      toast({
        title: 'Error',
        description: errorMessage,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const industries = [
    'Music & Entertainment',
    'Record Label',
    'Artist Management',
    'Music Production',
    'Audio Technology',
    'Media & Broadcasting',
    'Event Management',
    'Music Education',
    'Publishing',
    'Other'
  ];

  return (
    <Flex alignItems="center" justifyContent="center" minH="100vh" p={{ base: 2, md: 4 }}>
      <Container maxW={{ base: "full", md: "4xl" }} centerContent>
        <Box
          bg="dark.500"
          p={{ base: 4, md: 8, lg: 12 }}
          rounded="xl"
          w="full"
          maxW={{ base: "full", md: "3xl" }}
          boxShadow="2xl"
          border="1px solid"
          borderColor="secondary.700"
        >
          <VStack spacing={{ base: 6, md: 8 }} align="stretch" w="full">
            {/* Header Section */}
            <VStack spacing={4} textAlign="center">
              <Box
                p={3}
                rounded="full"
                bg="gradient-to-br from-blue.500 to-purple.600"
                display="inline-flex"
                alignItems="center"
                justifyContent="center"
                boxShadow="lg"
              >
                <Icon as={FiBriefcase} w={6} h={6} color="white" />
              </Box>

              <VStack spacing={2}>
                <Heading
                  size={{ base: "md", md: "lg" }}
                  color="primary.100"
                  fontFamily="heading"
                  textAlign="center"
                >
                  Merchant Registration
                </Heading>
                <Text
                  color="primary.900"
                  fontSize={{ base: "sm", md: "md" }}
                  textAlign="center"
                  maxW="md"
                  lineHeight="1.6"
                >
                  Complete your merchant profile to start managing artists and business operations
                </Text>
              </VStack>
            </VStack>

            {/* Stepper Navigation */}
            <Box w="full" mb={{ base: 4, md: 6 }}>
              {/* Mobile Progress Bar */}
              <Box display={{ base: "block", md: "none" }} mb={4}>
                <HStack justify="space-between" mb={2}>
                  <Text fontSize="sm" fontWeight="medium" color="primary.100">
                    Step {currentStep + 1} of {steps.length}
                  </Text>
                  <Text fontSize="sm" color="primary.900">
                    {Math.round(((currentStep + 1) / steps.length) * 100)}%
                  </Text>
                </HStack>
                <Progress
                  value={((currentStep + 1) / steps.length) * 100}
                  size="md"
                  colorScheme="blue"
                  bg="secondary.900"
                  borderRadius="full"
                />
                <Text fontSize="sm" fontWeight="semibold" color="primary.100" mt={2}>
                  {steps[currentStep].title}
                </Text>
                <Text fontSize="xs" color="primary.900" lineHeight="1.4">
                  {steps[currentStep].description}
                </Text>
              </Box>

              {/* Desktop Stepper */}
              <Box display={{ base: "none", md: "block" }}>
                <Stepper
                  index={activeStep}
                  colorScheme="blue"
                  size={{ base: "md", lg: "lg" }}
                  orientation="horizontal"
                >
                  {steps.map((step, index) => (
                    <Step key={index}>
                      <StepIndicator bg="secondary.900" borderColor="secondary.700">
                        <StepStatus
                          complete={<StepIcon color="white" />}
                          incomplete={<StepNumber color="primary.900" />}
                          active={<StepNumber color="white" />}
                        />
                      </StepIndicator>

                      <Box flexShrink="0">
                        <StepTitle
                          color="primary.100"
                          fontSize={{ base: "sm", lg: "md" }}
                          lineHeight="1.2"
                        >
                          {step.title}
                        </StepTitle>
                        <StepDescription
                          color="primary.900"
                          fontSize={{ base: "xs", lg: "sm" }}
                          lineHeight="1.2"
                        >
                          {step.description}
                        </StepDescription>
                      </Box>

                      <StepSeparator bg="secondary.700" />
                    </Step>
                  ))}
                </Stepper>
              </Box>
            </Box>

            {/* Step Content */}
            {currentStep === 0 && (
              <Box as="form" onSubmit={handleSubmit(() => setCurrentStep(1))}>
                <VStack spacing={{ base: 5, md: 6 }} w="full">
                  {/* Required Information Section */}
                  <Box w="full" p={{ base: 4, md: 6 }} bg="secondary.900" borderRadius="lg" border="1px" borderColor="secondary.700">
                    <Text fontSize={{ base: "sm", md: "md" }} fontWeight="bold" color="primary.100" mb={4}>
                      Required Information
                    </Text>
                    <Stack spacing={4}>
                      <FormControl isInvalid={errors.companyName} isRequired>
                        <FormLabel fontSize="sm" fontWeight="medium" color="primary.900">
                          <HStack spacing={2}>
                            <Icon as={FiBriefcase} color="dark.900" />
                            <Text>Company Name</Text>
                          </HStack>
                        </FormLabel>
                        <Input
                          placeholder="Enter your company name"
                          size="lg"
                          variant="outline"
                          bg="secondary.900"
                          color="primary.100"
                          _placeholder={{ color: "primary.900" }}
                          {...register('companyName', {
                            required: 'Company name is required',
                            minLength: {
                              value: 2,
                              message: 'Company name must be at least 2 characters'
                            }
                          })}
                        />
                        <FormErrorMessage color="red.400">
                          {errors.companyName && errors.companyName.message}
                        </FormErrorMessage>
                      </FormControl>

                      <FormControl isInvalid={errors.businessAddress} isRequired>
                        <FormLabel fontSize="sm" fontWeight="medium" color="primary.900">
                          <HStack spacing={2}>
                            <Icon as={FiMapPin} color="dark.900" />
                            <Text>Business Address</Text>
                          </HStack>
                        </FormLabel>
                        <Textarea
                          placeholder="Enter your complete business address"
                          rows={3}
                          variant="outline"
                          bg="secondary.900"
                          color="primary.100"
                          _placeholder={{ color: "primary.900" }}
                          resize="vertical"
                          {...register('businessAddress', {
                            required: 'Business address is required',
                            minLength: {
                              value: 10,
                              message: 'Please provide a complete address'
                            }
                          })}
                        />
                        <FormErrorMessage color="red.400">
                          {errors.businessAddress && errors.businessAddress.message}
                        </FormErrorMessage>
                      </FormControl>
                    </Stack>
                  </Box>

                  {/* Optional Information Section */}
                  <Box w="full" p={{ base: 4, md: 6 }} bg="secondary.900" borderRadius="lg" border="1px" borderColor="secondary.700">
                    <Text fontSize={{ base: "sm", md: "md" }} fontWeight="bold" color="primary.100" mb={4}>
                      Additional Information (Optional)
                    </Text>
                    <Stack spacing={4}>
                      <FormControl>
                        <FormLabel fontSize="sm" fontWeight="medium" color="primary.900">Industry</FormLabel>
                        <Select
                          placeholder="Select your industry"
                          variant="outline"
                          bg="secondary.900"
                          color="primary.100"
                          size="lg"
                          {...register('industry')}
                        >
                          {industries.map((industry) => (
                            <option key={industry} value={industry} style={{ backgroundColor: '#5C5C5C', color: '#FCFCFC' }}>
                              {industry}
                            </option>
                          ))}
                        </Select>
                      </FormControl>

                      <Stack direction={{ base: "column", lg: "row" }} spacing={4} w="full">
                        <FormControl>
                          <FormLabel fontSize="sm" fontWeight="medium" color="primary.900">
                            Registration Number
                          </FormLabel>
                          <Input
                            placeholder="Company registration number"
                            size="lg"
                            variant="outline"
                            bg="secondary.900"
                            color="primary.100"
                            _placeholder={{ color: "primary.900" }}
                            {...register('registrationNumber')}
                          />
                        </FormControl>

                        <FormControl>
                          <FormLabel fontSize="sm" fontWeight="medium" color="primary.900">Tax ID</FormLabel>
                          <Input
                            placeholder="Tax identification number"
                            size="lg"
                            variant="outline"
                            bg="secondary.900"
                            color="primary.100"
                            _placeholder={{ color: "primary.900" }}
                            {...register('taxId')}
                          />
                        </FormControl>
                      </Stack>

                      <Stack direction={{ base: "column", lg: "row" }} spacing={4} w="full">
                        <FormControl>
                          <FormLabel fontSize="sm" fontWeight="medium" color="primary.900">
                            <HStack spacing={2}>
                              <Icon as={FiPhone} color="dark.900" />
                              <Text>Phone Number</Text>
                            </HStack>
                          </FormLabel>
                          <Input
                            placeholder="Company phone number"
                            type="tel"
                            size="lg"
                            variant="outline"
                            bg="secondary.900"
                            color="primary.100"
                            _placeholder={{ color: "primary.900" }}
                            {...register('phone')}
                          />
                        </FormControl>

                        <FormControl>
                          <FormLabel fontSize="sm" fontWeight="medium" color="primary.900">
                            <HStack spacing={2}>
                              <Icon as={FiGlobe} color="dark.900" />
                              <Text>Website</Text>
                            </HStack>
                          </FormLabel>
                          <Input
                            placeholder="https://your-company.com"
                            type="url"
                            size="lg"
                            variant="outline"
                            bg="secondary.900"
                            color="primary.100"
                            _placeholder={{ color: "primary.900" }}
                            {...register('website')}
                          />
                        </FormControl>
                      </Stack>
                    </Stack>
                  </Box>

                  {/* Step 1 Navigation */}
                  <VStack spacing={3} w="full" pt={2}>
                    <Button
                      type="submit"
                      width="100%"
                      size={{ base: "md", md: "lg" }}
                      bg="gradient-to-r from-blue.500 to-blue.600"
                      color="white"
                      isDisabled={!watchedFields.companyName || !watchedFields.businessAddress}
                      _hover={{
                        bg: "gradient-to-r from-blue.600 to-blue.700",
                        transform: "translateY(-1px)",
                        boxShadow: "lg"
                      }}
                      _disabled={{
                        bg: "gray.600",
                        cursor: "not-allowed",
                        _hover: { bg: "gray.600", transform: "none" }
                      }}
                      transition="all 0.2s"
                      fontWeight="semibold"
                    >
                      Continue to Documents
                    </Button>

                    {onBack && (
                      <Button
                        variant="ghost"
                        size={{ base: "md", md: "lg" }}
                        w="full"
                        color="primary.900"
                        onClick={onBack}
                        _hover={{
                          color: "primary.100",
                          bg: "secondary.900"
                        }}
                      >
                        Back
                      </Button>
                    )}
                  </VStack>
                </VStack>
              </Box>
            )}

            {/* Step 2: Document Upload */}
            {currentStep === 1 && (
              <VStack spacing={{ base: 5, md: 6 }} w="full">
                <Box w="full" textAlign="center" p={{ base: 4, md: 6 }} bg="secondary.900" borderRadius="lg" border="1px" borderColor="secondary.700">
                  <Icon as={FiFileText} w={8} h={8} color="dark.900" mb={3} />
                  <Text fontSize={{ base: "md", md: "lg" }} fontWeight="bold" color="primary.100" mb={2}>
                    Upload Verification Documents
                  </Text>
                  <Text fontSize={{ base: "sm", md: "md" }} color="primary.900" lineHeight="1.6">
                    Please upload the required documents to verify your business. All documents are securely stored and reviewed by our team.
                  </Text>
                </Box>

                <VStack spacing={{ base: 3, md: 4 }} w="full">
                  {documentTypes.map((docType) => (
                    <DocumentUploadCard
                      key={docType.key}
                      documentType={docType}
                      onUpload={handleDocumentUpload}
                      progress={uploadProgress[docType.key]}
                      uploadedDocs={getUploadedDocuments(docType.key)}
                      isUploading={uploadProgress[docType.key] !== undefined && uploadProgress[docType.key] < 100}
                      error={uploadErrors[docType.key]}
                    />
                  ))}
                </VStack>

                {/* Required Documents Progress */}
                <Box w="full" p={{ base: 4, md: 6 }} bg="secondary.900" borderRadius="lg" border="1px" borderColor="secondary.700">
                  <HStack justify="space-between" mb={2}>
                    <Text fontSize="sm" color="primary.900" fontWeight="medium">
                      Required Documents Progress
                    </Text>
                    <Text fontSize="sm" color="primary.100" fontWeight="bold">
                      {documentTypes.filter(doc => doc.required && getUploadedDocuments(doc.key).length > 0).length}/
                      {documentTypes.filter(doc => doc.required).length}
                    </Text>
                  </HStack>
                  <Progress
                    value={(documentTypes.filter(doc => doc.required && getUploadedDocuments(doc.key).length > 0).length / documentTypes.filter(doc => doc.required).length) * 100}
                    size="sm"
                    colorScheme="blue"
                    bg="dark.900"
                    borderRadius="md"
                  />
                </Box>

                {/* Step 2 Navigation */}
                <VStack spacing={3} w="full" pt={2}>
                  <Button
                    width="100%"
                    size={{ base: "md", md: "lg" }}
                    variant="brand"
                    onClick={() => setCurrentStep(2)}
                    isDisabled={!getRequiredDocumentsUploaded()}
                    _hover={{
                      transform: "translateY(-1px)",
                      boxShadow: "lg"
                    }}
                    transition="all 0.2s"
                  >
                    Continue to Review
                  </Button>

                  <Button
                    variant="ghost"
                    size={{ base: "md", md: "lg" }}
                    w="full"
                    color="primary.900"
                    onClick={() => setCurrentStep(0)}
                    _hover={{
                      color: "dark.900",
                      bg: "transparent"
                    }}
                  >
                    Back to Company Info
                  </Button>
                </VStack>
              </VStack>
            )}

            {/* Step 3: Review and Submit */}
            {currentStep === 2 && (
              <Box as="form" onSubmit={handleSubmit(onSubmit)}>
                <VStack spacing={{ base: 5, md: 6 }} w="full">
                  <Box w="full" textAlign="center" p={{ base: 4, md: 6 }} bg="secondary.900" borderRadius="lg" border="1px" borderColor="secondary.700">
                    <Icon as={FiCheckCircle} w={8} h={8} color="dark.900" mb={3} />
                    <Text fontSize={{ base: "md", md: "lg" }} fontWeight="bold" color="primary.100" mb={2}>
                      Review and Submit
                    </Text>
                    <Text fontSize={{ base: "sm", md: "md" }} color="primary.900" lineHeight="1.6">
                      Please review your information before submitting. Once submitted, your application will be reviewed by our team.
                    </Text>
                  </Box>

                  {/* Company Info Summary */}
                  <Box w="full" p={{ base: 4, md: 6 }} bg="secondary.900" borderRadius="lg" border="1px" borderColor="secondary.700">
                    <HStack mb={4}>
                      <Icon as={FiBriefcase} color="dark.900" />
                      <Text fontSize={{ base: "sm", md: "md" }} fontWeight="bold" color="primary.100">
                        Company Information
                      </Text>
                    </HStack>
                    <Stack spacing={3}>
                      <HStack justify="space-between" align="start">
                        <Text fontSize="sm" color="primary.900" fontWeight="medium" minW="120px">Company Name:</Text>
                        <Text fontSize="sm" color="primary.100" textAlign="right" flex={1}>
                          {watchedFields.companyName || 'Not provided'}
                        </Text>
                      </HStack>
                      <HStack justify="space-between" align="start">
                        <Text fontSize="sm" color="primary.900" fontWeight="medium" minW="120px">Business Address:</Text>
                        <Text fontSize="sm" color="primary.100" textAlign="right" flex={1}>
                          {watchedFields.businessAddress || 'Not provided'}
                        </Text>
                      </HStack>
                      {watchedFields.industry && (
                        <HStack justify="space-between" align="start">
                          <Text fontSize="sm" color="primary.900" fontWeight="medium" minW="120px">Industry:</Text>
                          <Text fontSize="sm" color="primary.100" textAlign="right" flex={1}>
                            {watchedFields.industry}
                          </Text>
                        </HStack>
                      )}
                      {watchedFields.registrationNumber && (
                        <HStack justify="space-between" align="start">
                          <Text fontSize="sm" color="primary.900" fontWeight="medium" minW="120px">Registration #:</Text>
                          <Text fontSize="sm" color="primary.100" textAlign="right" flex={1}>
                            {watchedFields.registrationNumber}
                          </Text>
                        </HStack>
                      )}
                      {watchedFields.taxId && (
                        <HStack justify="space-between" align="start">
                          <Text fontSize="sm" color="primary.900" fontWeight="medium" minW="120px">Tax ID:</Text>
                          <Text fontSize="sm" color="primary.100" textAlign="right" flex={1}>
                            {watchedFields.taxId}
                          </Text>
                        </HStack>
                      )}
                      {watchedFields.phone && (
                        <HStack justify="space-between" align="start">
                          <Text fontSize="sm" color="primary.900" fontWeight="medium" minW="120px">Phone:</Text>
                          <Text fontSize="sm" color="primary.100" textAlign="right" flex={1}>
                            {watchedFields.phone}
                          </Text>
                        </HStack>
                      )}
                      {watchedFields.website && (
                        <HStack justify="space-between" align="start">
                          <Text fontSize="sm" color="primary.900" fontWeight="medium" minW="120px">Website:</Text>
                          <Text fontSize="sm" color="primary.100" textAlign="right" flex={1}>
                            {watchedFields.website}
                          </Text>
                        </HStack>
                      )}
                    </Stack>
                  </Box>

                  {/* Documents Summary */}
                  <Box w="full" p={{ base: 4, md: 6 }} bg="secondary.900" borderRadius="lg" border="1px" borderColor="secondary.700">
                    <HStack mb={4}>
                      <Icon as={FiFileText} color="dark.900" />
                      <Text fontSize={{ base: "sm", md: "md" }} fontWeight="bold" color="primary.100">
                        Uploaded Documents
                      </Text>
                    </HStack>
                    <VStack align="stretch" spacing={3}>
                      {documentTypes.map((docType) => {
                        const uploadedDocs = getUploadedDocuments(docType.key);
                        return (
                          <HStack key={docType.key} justify="space-between" p={3} bg="dark.700" borderRadius="md">
                            <HStack spacing={3}>
                              <Text fontSize="sm" color="primary.900" fontWeight="medium">
                                {docType.label}
                              </Text>
                              {docType.required && (
                                <Badge colorScheme="orange" size="sm">Required</Badge>
                              )}
                            </HStack>
                            <HStack spacing={2}>
                              <Text fontSize="sm" color="primary.100">
                                {uploadedDocs.length} file(s)
                              </Text>
                              {uploadedDocs.length > 0 ? (
                                <Icon as={FiCheckCircle} color="green.400" boxSize={4} />
                              ) : (
                                <Icon as={FiX} color="red.400" boxSize={4} />
                              )}
                            </HStack>
                          </HStack>
                        );
                      })}
                    </VStack>
                  </Box>

                  {/* Required Documents Check */}
                  {!getRequiredDocumentsUploaded() && (
                    <Alert status="warning" bg="orange.900" borderColor="orange.700" borderRadius="lg">
                      <AlertIcon color="orange.300" />
                      <Box>
                        <AlertTitle color="orange.100" fontSize="sm">Missing Required Documents!</AlertTitle>
                        <AlertDescription color="orange.200" fontSize="sm">
                          Please upload all required documents before submitting your application.
                        </AlertDescription>
                      </Box>
                    </Alert>
                  )}

                  {/* Step 3 Navigation */}
                  <VStack spacing={3} w="full" pt={2}>
                    <Button
                      type="submit"
                      width="100%"
                      size={{ base: "md", md: "lg" }}
                      variant="brand"
                      isLoading={isLoading}
                      loadingText="Setting up your account..."
                      isDisabled={!isValid || !getRequiredDocumentsUploaded()}
                      _hover={{
                        transform: "translateY(-1px)",
                        boxShadow: "lg"
                      }}
                      transition="all 0.2s"
                    >
                      Complete Merchant Setup
                    </Button>

                    <Button
                      variant="ghost"
                      size={{ base: "md", md: "lg" }}
                      w="full"
                      color="primary.900"
                      onClick={() => setCurrentStep(1)}
                      isDisabled={isLoading}
                      _hover={{
                        color: "dark.900",
                        bg: "transparent"
                      }}
                    >
                      Back to Documents
                    </Button>
                  </VStack>
                </VStack>
              </Box>
            )}

            {/* Footer */}
            <Box textAlign="center" pt={{ base: 4, md: 6 }}>
              <Text fontSize="xs" color="primary.900" lineHeight="1.5">
                By completing setup, you agree to our{' '}
                <Text as="span" color="dark.900" textDecoration="underline" cursor="pointer">
                  Terms of Service
                </Text>
                {' '}and{' '}
                <Text as="span" color="dark.900" textDecoration="underline" cursor="pointer">
                  Privacy Policy
                </Text>
              </Text>
            </Box>
          </VStack>
        </Box>
      </Container>
    </Flex>
  );
};

export default CompanyInfoStep;